"use client"

import * as React from "react"

export type Theme = "dark" | "light"

export const THEME_STORAGE_KEY = "workflows-theme"

/**
 * Minimal theme state.
 *
 * Replaces next-themes, which renders its no-flash <script> from inside a
 * client component. React 19 warns about that ("Encountered a script tag while
 * rendering React component") and no prop suppresses it. Here the script lives
 * in the server-rendered layout instead, and this file only owns state.
 *
 * `mounted` is exposed because the server cannot know the stored theme: any UI
 * that renders differently per theme must wait for it, or the markup will not
 * match on hydration.
 */
type ThemeState = {
  theme: Theme
  resolvedTheme: Theme
  setTheme: (t: Theme) => void
  toggle: () => void
  mounted: boolean
}

const Ctx = React.createContext<ThemeState | null>(null)

export function ThemeProvider({
  children,
  defaultTheme = "dark",
}: {
  children: React.ReactNode
  defaultTheme?: Theme
}) {
  const [theme, setThemeState] = React.useState<Theme>(defaultTheme)
  const [mounted, setMounted] = React.useState(false)

  // Adopt whatever the pre-paint script already applied to <html>.
  React.useEffect(() => {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY) as Theme | null
    const active: Theme =
      stored === "light" || stored === "dark"
        ? stored
        : document.documentElement.classList.contains("dark")
          ? "dark"
          : defaultTheme
    setThemeState(active)
    setMounted(true)
  }, [defaultTheme])

  React.useEffect(() => {
    if (!mounted) return
    document.documentElement.classList.toggle("dark", theme === "dark")
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, theme)
    } catch {
      // Private mode / blocked storage — the theme still applies for this visit.
    }
  }, [theme, mounted])

  const setTheme = React.useCallback((t: Theme) => setThemeState(t), [])
  const toggle = React.useCallback(
    () => setThemeState((t) => (t === "dark" ? "light" : "dark")),
    [],
  )

  const value = React.useMemo(
    () => ({ theme, resolvedTheme: theme, setTheme, toggle, mounted }),
    [theme, setTheme, toggle, mounted],
  )

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useTheme(): ThemeState {
  const ctx = React.useContext(Ctx)
  if (!ctx) throw new Error("useTheme must be used inside <ThemeProvider>")
  return ctx
}

/**
 * Runs before first paint, from the server-rendered layout, so a stored theme
 * is applied without a flash of the wrong one.
 */
export const THEME_INIT_SCRIPT = `
(function(){
  try {
    var t = localStorage.getItem('${THEME_STORAGE_KEY}');
    if (t !== 'light' && t !== 'dark') t = 'dark';
    document.documentElement.classList.toggle('dark', t === 'dark');
  } catch (e) {
    document.documentElement.classList.add('dark');
  }
})();
`
